<template>
    <div class="editor notranslate" @focusout="onFocusOut" @focusin="onFocusIn">
        <div class="sender">
            <label>{{$t('messageEditor.from')}}</label>
            <span><b-select size="sm" :options="senderAddresses" v-model="senderAddress"></b-select></span>
        </div>
        <div class="recipients" ref="rcpts">
            <recipient-editor :label="$t('messageEditor.to')" v-model="rcptsTo" ref="rcptsTo">
                <span tabindex="0" v-show="!showCc" @click="showCc=true">{{$t('messageEditor.cc')}}</span>
                <span tabindex="0" v-show="!showBcc && bccsAllowed" @click="showBcc=true">{{$t('messageEditor.bcc')
                    }}</span>
            </recipient-editor>
            <recipient-editor :label="'Cc'" v-if="showCc" @hide="showCc=false" v-model="rcptsCc"></recipient-editor>
            <recipient-editor :label="'Bcc'" v-if="showBcc && bccsAllowed" @hide="showBcc=false"
                    v-model="rcptsBcc"></recipient-editor>
        </div>
        <div class="subject">
            <input :value="subject" @input="$emit('update:subject', $event.target.value)" ref="subject"
                    :placeholder="$t('messageEditor.subjectPlaceholder')">
        </div>
        <ToolbarWrapper ref="toolbar">
            <div class="toolbarSection">
            <icon-button icon="undo" @click="editor.undo()" tabindex="-1"
                    class="editbutton" :disabled="!canUndo || showSource" :aria="$t('messageEditor.aria.undo')"/>
            <icon-button icon="redo" @click="editor.redo()" tabindex="-1"
                    class="editbutton" :disabled="!canRedo || showSource" :aria="$t('messageEditor.aria.redo')"/>

            <icon-button icon="bold" @click="simple('B', 'bold')" :disabled="showSource" tabindex="-1"
                    :class="{ editbutton: true, isActive: hasFormat('B') }" :aria="$t('messageEditor.aria.bold')"/>
            <icon-button icon="italic" @click="simple('I', 'italic')" :disabled="showSource" tabindex="-1"
                    :class="{ editbutton: true, isActive: hasFormat('I') }" :aria="$t('messageEditor.aria.italic')"/>
            <icon-button icon="underline" @click="simple('U', 'underline')" :disabled="showSource" tabindex="-1"
                    :class="{ editbutton: true, isActive: hasFormat('U') }" :aria="$t('messageEditor.aria.underline')"/>
            <icon-button icon="strikethrough" @click="simple('S', 'strikethrough')" :disabled="showSource"
                    :class="{ editbutton: true, isActive: hasFormat('S') }" tabindex="-1"
                    :aria="$t('messageEditor.aria.strikethrough')"/>
            <icon-button icon="code" @click="doCode" :disabled="showSource" tabindex="-1"
                    :class="{ editbutton: true, isActive: hasFormat('pre') || hasFormat('code') }"
                    :aria="$t('messageEditor.aria.codeFormat')"/>
            </div>
            <div class="toolbarSection">
            <drop-menu-button icon="heading" menuname="messageEditor.headerMenu" :options="headerOptions"
                    class="editbutton" @menu="doMenu" :disabled="showSource" :triggerHide="triggerHide" tabindex="-1"
                    :aria="$t('messageEditor.aria.headerMenu')"/>
            <drop-menu-button icon="text-size" menuname="messageEditor.textSizeMenu" :options="textSizeOptions"
                    class="editbutton" @menu="doMenu" :disabled="showSource" :triggerHide="triggerHide" tabindex="-1"
                    :aria="$t('messageEditor.aria.textSizeMenu')"/>
            <drop-menu-button icon="font" menuname="messageEditor.fontMenu" :options="fontOptions"
                    class="editbutton" @menu="doMenu" :disabled="showSource" :triggerHide="triggerHide" tabindex="-1"
                    :aria="$t('messageEditor.aria.fontMenu')"/>

            <editor-colour-picker :disabled="showSource" name="messageEditor.textColour" :init="textColour"
                    tabindex="-1"
                @select="setTextColour" :triggerHide="triggerHide" :aria="$t('messageEditor.aria.textColourPicker')">
                <font-awesome-layers>
                    <font-awesome-icon icon="font" fixed-width/>
                    <font-awesome-icon icon="horizontal-rule" transform="down-9"
                            :style="{ color: textColour }" />
                </font-awesome-layers>
            </editor-colour-picker>
            <editor-colour-picker :disabled="showSource" name="messageEditor.highlightColour" :init="highlightColour"
                    @select="setHighlightColour" :triggerHide="triggerHide" tabindex="-1"
                    :aria="$t('messageEditor.aria.highlightColourPicker')">
                <font-awesome-layers>
                    <font-awesome-icon icon="square" transform="grow-5"
                            :style="{ color: highlightColour }"/>
                    <font-awesome-icon icon="highlighter" fixed-width/>
                </font-awesome-layers>
            </editor-colour-picker>
            </div>
            <div class="toolbarSection">

            <icon-button icon="list" @click="doUnorderedList()" :disabled="showSource" tabindex="-1"
                    :class="{ editbutton: true, isActive: hasFormat('ul') }" :aria="$t('messageEditor.aria.list')"/>
            <icon-button icon="list-ol" @click="doOrderedList()" :disabled="showSource" tabindex="-1"
                    :class="{ editbutton: true, isActive: hasFormat('ol') }"
                    :aria="$t('messageEditor.aria.orderedList')"/>
            <icon-button icon="quote-right" @click="doQuote()" :disabled="showSource" tabindex="-1"
                    class="editbutton" :aria="$t('messageEditor.aria.quote')"/>
            <icon-button icon="outdent" @click="doOutdent()" tabindex="-1"
                    class="editbutton" :aria="$t('messageEditor.aria.outdent')"
                    :disabled="showSource || !(hasFormat('ol') || hasFormat('ul') || hasFormat('blockquote'))"/>
            <icon-button icon="indent" @click="doIndent()" class="editbutton"  :disabled="showSource" tabindex="-1"
                    :aria="$t('messageEditor.aria.indent')"/>

            <drop-menu-button icon="align-left" menuname="messageEditor.justifyMenu" :options="justifyOptions"
                    class="editbutton" @menu="doMenu" :disabled="showSource" :triggerHide="triggerHide" tabindex="-1"
                    :aria="$t('messageEditor.aria.justifyOptions')"/>

            <icon-button icon="remove-format" @click="editor.removeAllFormatting()" :disabled="showSource" tabindex="-1"
                    class="editbutton" :aria="$t('messageEditor.aria.removeFormatting')"/>
            </div>

            <div class="toolbarSection">
                <icon-button icon="horizontal-rule" @click="editor.insertHTML('<hr>')" :disabled="showSource"
                        tabindex="-1"
                        class="editbutton" :aria="$t('messageEditor.aria.hr')"/>

                <drop-pane-button icon="link" panename="messageEditor.addLink" ref="addLink"
                        class="editbutton" :disabled="showSource" :tabindex="-1"
                        :class="{ isActive: hasFormat('a') }"
                        @beforeShow="loadAddLinkValue" @escape="addLinkValue=''"
                        @show="onShowAddLink" :triggerHide="triggerHide"
                        :aria="$t('messageEditor.aria.addLink')">
                    <form>
                        <input type="url" v-model="addLinkValue" ref="addLinkInput"
                                :placeholder="$t('messageEditor.addLink.placeholder')">
                        <button @click="doAddLink" :disabled="!addLinkOK" type="submit">
                            <font-awesome-icon icon="link"/>
                        </button>
                    </form>
                </drop-pane-button>

                <icon-button icon="unlink" @click="editor.removeLink()" :disabled="showSource || !hasFormat('a')"
                        tabindex="-1"
                        class="editbutton" :aria="$t('messageEditor.aria.removeLink')"/>

                <special-char-picker icon="omega" name="messageEditor.specialCharacter"
                        @select="editor.insertHTML($event)" tabindex="-1"
                        :aria="$t('messageEditor.aria.specialCharacter')"
                        :characters="['\u00a3\u00a5\u20ac\u00a2\u00ab\u00bb', '\u00a9\u00ae\u2122\u2126\u266b\u00b0',
                    '\u00b2\u00b3\u00b1\u2260\u221a\u221b', '\u00bc\u00bd\u00be\u00a1\u00bf\u00f7',
                    '\u00de\u00df\u2208\u221d\u221e\u00b6', '\u00b5\u2211\u03c0\u03a9\u03b8\u03b4']"/>
                <special-char-picker :icon="['far', 'smile']" name="messageEditor.emojis" tabindex="-1"
                        @select="editor.insertHTML($event)" :aria="$t('messageEditor.aria.emoji')"
                        :characters="['\uD83D\uDE01\uD83D\uDE02\uD83D\uDE03\uD83D\uDE04\uD83D\uDE05\uD83D\uDE06',
                     '\uD83D\uDE07\uD83D\uDE08\uD83D\uDE09\uD83D\uDE0A\uD83D\uDE0B\uD83D\uDE0C',
                    '\uD83D\uDE0D\uD83D\uDE0E\uD83D\uDE0F\uD83D\uDE10\uD83D\uDE11\uD83D\uDE12',
                    '\uD83D\uDE13\uD83D\uDE14\uD83D\uDE15\uD83D\uDE16\uD83D\uDE17\uD83D\uDE18']"/>

                <image-uploader :triggerHide="triggerHide" :disabled="showSource" @addImage="doAddImage" tabindex="-1"
                        :aria="$t('messageEditor.aria.addImage')"></image-uploader>
                <attachmentsEditor :triggerHide="triggerHide" :attachments="attachments" tabindex="-1"
                        @add="doAddAttachment" @delete="doDeleteAttachment" ref="attachmentsEditor"
                        :aria="$t('messageEditor.aria.manageAttachments')"></attachmentsEditor>

                <icon-button icon="file-code" @click="toggleSource" class="editbutton" tabindex="-1"
                        :aria="$t('messageEditor.aria.viewSource')"/>
            </div>
        </ToolbarWrapper>
        <div class="framewrap">
            <iframe @load="onIframeLoad" ref="iframe" :src="`${getBaseURL}/editordoc.html`"
                    v-show="!showSource"></iframe>
            <textarea v-show="showSource" v-model="editorContent"></textarea>
        </div>
        <div class="status">
            <div>{{path}}</div>
            <div></div>
        </div>
        <div class="bottombar">
            <div class="send">
                <b-button variant="primary" class="m-2" @click="doSend"
                    :disabled="sending || invalidMessage || (loadingAttachments > 0)">
                    <span v-if="!sending">
                        <font-awesome-icon icon="paper-plane"></font-awesome-icon>
                        {{$t('messageEditor.sendButton')}}
                    </span>
                    <font-awesome-icon v-if="sending" spin icon="spinner-third"></font-awesome-icon>
                    <span v-if="sending & sendProgress < 100">
                        {{$t('messageEditor.sending', { progress: sendProgress })}}
                    </span>
                    <span v-if="sending & sendProgress >= 100">
                        {{$t('messageEditor.processing')}}
                    </span>
                </b-button>
            </div>
            <div class="status">
                <div @click="$refs.attachmentsEditor.show()" class="attachments">
                  <span v-if="loadingAttachments" class="loadingattachments">
                    {{$t('messageEditor.loadingAttachments', { count: loadingAttachments })}}
                  </span>
                  <span v-else>
                    {{$t('messageEditor.attachmentInfo', {count:attachments.length, size: attachmentsSize})}}
                  </span>
                </div>
                <div class="messagesize">
                    {{$t('messageEditor.messageSize', {size: Math.round((editorContent.length + 99) / 100) * 100})}}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import upperFirst from 'lodash/upperFirst';
import debounce from 'lodash/debounce';
import DOMPurify from 'dompurify';
import IconButton from '@/components/iconButton';
import DropMenuButton from '@/components/DropMenuButton';
import DropPaneButton from '@/components/dropPaneButton';
import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import EditorColourPicker from '@/components/editorColourPicker';
import SpecialCharPicker from '@/components/specialCharPicker';
import ImageUploader from '@/components/imageUploader';
import AttachmentsEditor from '@/components/attachmentsEditor';
import axios from 'axios';
import RecipientEditor from '@/components/recipientEditor';
import Turndown from 'turndown';
import ParseAddrs from '@/helpers/parseAddrs';
import he from 'he';
import ToolbarWrapper from '@/components/toolbarWrapper';

export default {
  name: 'messageEditor',
  components: { EditorColourPicker,
    DropMenuButton,
    DropPaneButton,
    IconButton,
    FontAwesomeIcon,
    FontAwesomeLayers,
    SpecialCharPicker,
    ImageUploader,
    AttachmentsEditor,
    RecipientEditor,
    ToolbarWrapper },
  data() {
    return {
      editor: null,
      Squire: null,
      editorDoc: null,
      path: '',
      cursor: '',
      dropPoint: null,
      showSource: false,
      canUndo: false,
      canRedo: false,
      addLinkValue: '',
      addLinkRange: null,
      fileToLoad: null,
      textColour: 'rgb(0,0,0)',
      highlightColour: 'rgb(255,255,255)',
      triggerHide: false,
      attachments: [],
      editorContent: '',
      showCc: false,
      showBcc: false,
      rcptsTo: [],
      rcptsCc: [],
      rcptsBcc: [],
      sending: false,
      sendProgress: 0,
      senderAddress: '',
      loadingAttachments: 0
    };
  },
  props: {
    subject: String,
    message: Object
  },
  mounted() {
    /* this.editor = new Squire(this.$refs.editor,
      { sanitizeToDOMFragment: this.sanitize });
    this.editor.addEventListener('input', this.listener)
      .addEventListener('input', this.onInput)
      .addEventListener('cursor', this.onCursor)
      .addEventListener('pathChange', this.onPathChange)
      .addEventListener('undoStateChange', this.listener)
      .addEventListener('paste', this.onPaste)
      .addEventListener('willPaste', this.listener)
      .addEventListener('dragover', this.onDragOver)
      .addEventListener('drop', this.onDrop);
    this.editor.setHTML(this.content);
    this.content = this.editor.getHTML(); */
  },
  created() {
    console.log('created messageEditor', this.getBaseURL);
    if (this.message) {
      if (this.message.rcptsTo) {
        this.rcptsTo = this.mapRecipients(this.message.rcptsTo);
        console.log('msg rcptsto', this.rcptsTo);
      }
      let sig = this.$store.getters.sendingSignature;

      if (this.message.content) {
        this.editorContent = this.message.content;
      } else {
        this.editorContent = '<p></p>' + sig;
      }
      if (this.bccsAllowed) {
        this.rcptsBcc = this.mapRecipients(ParseAddrs(this.$store.state.settings.autobcc));
      }
    }
    this.senderAddress = this.$store.getters.senderEmailAddresses.split('\n')[0];
    console.log('set sender address = ', this.senderAddress);
    console.log('message', this.message);
    if (this.message.attachments) {
      this.$nextTick(() => {
        console.log('load Attachments');
        this.message.attachments.forEach(a => {
          console.log('add attachment', a, a.name);
          this.loadingAttachments++;
          axios.get(a.url)
            .then(d => {
              console.log('got attachment', a, d.data.data);

              const i = d.data.data.indexOf(',');
              this.doAddAttachment({ filename: a.name, data: d.data.data.substr(i + 1), type: a.type });
            })
            .finally(() => {
              this.loadingAttachments--;
            });
        });
        console.log('attachments', this.attachments);
      });
    }
  },
  methods: {
    mapRecipients(rcpts) {
      console.log('mapRecipients', rcpts);
      return rcpts.map(r => {
        if ((r.nameAuto) || (!r.name)) {
          return r.addr;
        } else {
          const name = r.name.replace(/([\\"])/g, '\\$1');
          return `"${name}" <${r.addr}>`;
        }
      });
    },
    listener(e) {
      console.log('editor event', e.type, e);
    },
    onFocusIn(event) {
      console.log('in', event);
    },
    onFocusOut(event) {
      console.log('out', event);
      if ((this.$refs.rcpts.contains(event.target)) &&
        ((!event.relatedTarget) || (!this.$refs.rcpts.contains(event.relatedTarget)))) {
        this.showCc = this.rcptsCc.length !== 0;
        // this.showBcc = this.rcptsBcc.length !== 0;
      }
      if ((this.$refs.toolbar.$el.contains(event.target)) &&
        ((!event.relatedTarget) || (!this.$refs.toolbar.$el.contains(event.relatedTarget)))) {
        console.log('close dropdowns');
        this.closeAnyDropdowns();
      }
    },
    sanitize(html, isPaste, squire) {
      var frag = html ? DOMPurify.sanitize(html, {
        ALLOW_UNKNOWN_PROTOCOLS: true,
        WHOLE_DOCUMENT: false,
        RETURN_DOM: true,
        RETURN_DOM_FRAGMENT: true
      }) : null;
      return frag ? this.editorDoc.importNode(frag, true) : this.editorDoc.createDocumentFragment();
    },
    onIframeLoad() {
      console.log('iFrame loaded');
      const iframe = this.$refs.iframe;
      this.editorDoc = iframe.contentDocument;
      this.Squire = iframe.contentWindow.Squire;
      this.editor = iframe.contentWindow.editor;
      this.editor
        .addEventListener('input', this.onInput)
        .addEventListener('cursor', this.onCursor)
        .addEventListener('pathChange', this.onPathChange)
        .addEventListener('undoStateChange', this.onUndoStateChange)
        .addEventListener('paste', this.onPaste)
        .addEventListener('willPaste', this.listener)
        .addEventListener('dragover', this.onDragOver)
        .addEventListener('drop', this.onDrop);
      this.editor.setKeyHandler('alt-t', this.doHotKey);
      this.editor.setKeyHandler('alt-s', this.doHotKey);
      this.editor.setConfig({ sanitizeToDOMFragment: this.sanitize });
      this.editor.setHTML(this.editorContent);
      this.editorContent = this.editor.getHTML();
    },
    doHotKey(squire, event, range) {
      console.log('hot key', event);
      if ((event.key === 't') && (event.altKey)) {
        this.$refs.rcptsTo.focus();
      } else if ((event.key === 's') && (event.altKey)) {
        this.$refs.subject.focus();
      }
    },
    onPathChange({ path }) {
      this.path = path;
    },
    onInput: debounce(function() {
      this.editorContent = this.editor.getHTML();
    }, 500, { maxWait: 10000 }),
    onCursor(event) {
      this.cursor = event.range.startOffset;
    },
    closeAnyDropdowns() {
      this.triggerHide = true;
      this.$nextTick(() => {
        this.triggerHide = false;
      });
    },
    onUndoStateChange(event) {
      ({ canUndo: this.canUndo, canRedo: this.canRedo } = event);
    },
    hasFormat(fmt) {
      if (!this.editor) {
        return false;
      }
      return this.editor.hasFormat(fmt);
    },
    simple(fmt, func) {
      if (this.editor.hasFormat(fmt)) {
        this.editor[`remove${upperFirst(func)}`]();
      } else {
        this.editor[func]();
      }
    },
    doUnorderedList() {
      if (this.editor.hasFormat('ul')) {
        this.editor.removeList();
      } else {
        this.editor.makeUnorderedList();
      }
    },
    doOrderedList() {
      if (this.editor.hasFormat('ol')) {
        this.editor.removeList();
      } else {
        this.editor.makeOrderedList();
      }
    },
    doQuote() {
      this.editor.increaseQuoteLevel();
    },
    doCode() {
      this.editor.toggleCode();
    },
    doIndent() {
      if (this.editor.hasFormat('ul') || this.editor.hasFormat('ol')) {
        this.editor.increaseListLevel();
      } else {
        this.editor.modifyBlocks(fragment => {
          return this.editor.createElement('BLOCKQUOTE',
            { class: 'indent' }, [fragment]);
        });

        // this.editor.increaseQuoteLevel();
      }
    },
    doOutdent() {
      if (this.editor.hasFormat('ul') || this.editor.hasFormat('ol')) {
        this.editor.decreaseListLevel();
      } else {
        console.log('squire', this.Squire);
        const root = this.editor._root;

        this.editor.modifyBlocks(fragment => {
          var indents = fragment.querySelectorAll('blockquote');
          console.log('indents', indents);
          Array.prototype.filter.call(indents, el => {
            return !this.Squire.getNearest(el.parentNode, root, 'BLOCKQUOTE');
          })
            .forEach(el => {
              this.Squire.replaceWith(el, this.Squire.empty(el));
            });
          return fragment;
        });
      }
    },
    doMenu(event) {
      this.editor.focus();

      if (event.substr(0, 8) === 'justify.') {
        this.editor.setTextAlignment(event.substr(8));
      } else if (event.substr(0, 7) === 'header.') {
        this.editor.modifyBlocks(fragment => {
          console.log('doMenu header');
          console.dir(fragment);
          Array.prototype.filter.call(fragment.childNodes, el => {
            return this.Squire.isBlock(el);
          })
            .forEach(el => {
              this.Squire.replaceWith(el, this.Squire.empty(el));
            });
          return this.editor.createElement(event.substr(7), {}, [fragment]);
        });
      } else if (event.substr(0, 9) === 'textsize.') {
        this.editor.setFontSize(event.substr(9));
      } else if (event.substr(0, 5) === 'font.') {
        this.editor.setFontFace(this.$t(`messageEditor.fontMenu.fonts_${event.substr(5)}`));
      }
    },
    onShowAddLink() {
      this.$nextTick(() => {
        this.$refs.addLinkInput.focus();
        this.editorDoc.getElementById('other').focus();
        console.log(this.editorDoc.activeElement);
      });
    },
    loadAddLinkValue() {
      const r = this.editor.getSelection();
      this.addLinkRange = null;
      if (this.hasFormat('a')) {
        console.dir(r);
        const root = this.editor._root;
        const a = this.Squire.getNearest(r.startContainer, root, 'A');
        if (a) { // not sure this works right if the previous link node contains multiple other nodes
          console.dir(a);
          this.addLinkValue = a.href;
          if (a.childNodes.length > 0) {
            let newRange = this.editorDoc.createRange();
            newRange.setStart(a.childNodes[0], 0);
            newRange.setEnd(a.childNodes[a.childNodes.length - 1], a.childNodes[a.childNodes.length - 1].length);
            this.addLinkRange = newRange;
          }
          return;
        }
      }
      if (r.startContainer.nodeName === '#text') {
        const text = r.startContainer.textContent.substring(r.startOffset, r.endOffset);
        if (/^((f|ht)tps:\/\/|www\.)/i.test(text)) {
          this.addLinkValue = text;
        }
      }
    },
    doAddLink(event) {
      if (this.addLinkRange) {
        console.log('set selection');
        console.dir(this.addLinkRange);
        this.editor.setSelection(this.addLinkRange);
      }
      if (this.hasFormat('a')) {
        this.editor.removeLink();
      }
      if (this.addLinkValue.substr(0, 4) === 'www.') {
        this.addLinkValue = 'http://' + this.addLinkValue;
      } else if (this.addLinkValue.indexOf('@') !== -1) {
        this.addLinkValue = 'mailto:' + this.addLinkValue;
      }
      this.editor.makeLink(this.addLinkValue, { rel: 'nofollow noreferer noopener', target: '_blank' });
      this.$refs.addLink.hide();
      this.addLinkValue = '';
    },
    setTextColour(colour) {
      this.textColour = colour;
      this.editor.setTextColour(colour);
    },
    setHighlightColour(colour) {
      this.highlightColour = colour;
      this.editor.setHighlightColour(colour);
    },
    onPaste(event) {
      const items = (event.clipboardData || event.originalEvent.clipboardData).items;
      console.log(JSON.stringify(items)); // will give you the mime types
      for (const item of items) {
        if (item.kind === 'file') {
          const blob = item.getAsFile();
          const reader = new FileReader();
          reader.onload = event => {
            console.log('paste data', event.target.result);
            this.editor.insertImage(event.target.result);
          }; // data url!
          reader.readAsDataURL(blob);
          event.stopPropagation();
          event.preventDefault();
        }
      }
    },
    doAddImage({ src, alt }) {
      console.log('doAddImage');
      this.editor.insertImage(src, { alt });
    },
    doAddAttachment(attach) {
      this.attachments.push(attach);
    },
    doDeleteAttachment(attachmentName) {
      for (let i = 0; i < this.attachments.length; i++) {
        if (this.attachments[i].filename === attachmentName) {
          this.attachments.splice(i, 1);
          break;
        }
      }
    },
    onDrop(event) {
      console.log('drop - ', event);
      event.stopPropagation();
      event.preventDefault();

      let totalSize = 0;
      const files = (event.dataTransfer || event.originalEvent.dataTransfer).files;
      for (const file of files) {
        totalSize += file.size;
      }
      if (totalSize > 10 * 1024 * 1024) {
        this.$dialog.alert(this.$t('messageEditor.dropTooBig', { size: totalSize }));
        return;
      }

      if (this.dropPoint) {
        let range = new Range();
        range.setStart(this.dropPoint.node, this.dropPoint.offset);
        range.collapse(true);
        this.editor.setSelection(range);
        this.dropPoint = null;
      }

      console.log(JSON.stringify(files)); // will give you the mime types
      for (const file of files) {
        console.log('drop item ', file);
        console.dir(JSON.stringify(file));
        const thisFileType = file.type;
        const thisFilename = file.name;
        // const blob = item.getAsFile();
        const reader = new FileReader();
        reader.onload = event => {
          console.log('drop data', event.target);
          if (thisFileType.split('/')[0] === 'image') {
            this.editor.insertImage(event.target.result, { alt: thisFilename });
          } else {
            this.doAddAttachment({ filename: thisFilename, data: event.target.result, type: thisFileType });
          }
        }; // data url!
        reader.readAsDataURL(file);
      }
    },
    onDragOver(event) {
      // remember dragover point for a future drop
      if (this.editorDoc.caretPositionFromPoint) {
        const range = this.editorDoc.caretPositionFromPoint(event.clientX, event.clientY);
        this.dropPoint = { node: range.offsetNode, offset: range.offset };
      } else if (this.editorDoc.caretRangeFromPoint) {
        const range = this.editorDoc.caretRangeFromPoint(event.clientX, event.clientY);
        this.dropPoint = { node: range.startContainer, offset: range.startOffset };
      }
    },
    toggleSource() {
      if (this.showSource) {
        this.editor.setHTML(this.editorContent);
      }
      this.showSource = !this.showSource;
    },
    convertImagesToAttachments() {
      console.log('convert images');
      const editorRoot = this.editor.getDocument().cloneNode(true);
      console.dir(this.editor.getDocument());
      console.dir(editorRoot);
      const images = editorRoot.images;
      let contentId = 1;
      let now = new Date();
      let dom = this.$store.state.senderAddress;
      dom = dom.substr(dom.indexOf('@') + 1);
      for (let i = 0; i < images.length; i++) {
        if (images[i].src.substr(0, 5) === 'data:') {
          const x = images[i].src.indexOf(',');
          const type = images[i].src.substring(5, x - 7);
          const contentIdStr = `part_${contentId++}_${now.getTime()}_${performance.now()}@${dom}`;
          this.attachments.push({ filename: images[i].alt,
            type,
            data: images[i].src.substr(x + 1),
            contentid: contentIdStr });
          images[i].src = `cid:${contentIdStr}`;
        }
      }
      console.log('after replace images', editorRoot);
      console.dir(editorRoot);
      console.log(this.editor.getDocument());
      console.log(this.editor._root);
      console.log(this.editor._root.innerHTML);
      console.log(editorRoot.body.innerHTML);
      return editorRoot.body.innerHTML;
    },
    doSend() {
      this.sending = true;
      this.sendProgress = 0;

      let content = this.convertImagesToAttachments();

      const td = new Turndown();

      // remove <br/> just before </div> or </h?> or </li>
      content = content.replace(/<br\/?>(<\/div>|<\/h[1-6]>|<\/li>)/ig, '$1');
      const markdown = td.turndown(content);
      console.log(content, markdown);
      const stylesheet = this.getEditorStyleSheet();
      console.log(content);
      console.log(he.encode(content));
      console.log(he.encode(content, { useNamedReferences: true, allowUnsafeSymbols: true }));
      const htmlContent = he.encode(
        `<html><head><title>${this.subject}</title><style>${stylesheet}</style></head>\r\n` +
        `<body>${content}</body></html>`, { useNamedReferences: true, allowUnsafeSymbols: true });
      content = '';
      console.log(this.processRecipients(this.rcptsTo));
      let msgData = { message: htmlContent,
        text: markdown,
        fromAddr: this.senderAddress,
        fromName: this.$store.state.settings.realname,
        to: this.processRecipients(this.rcptsTo),
        cc: this.processRecipients(this.rcptsCc),
        subject: this.subject,
        attachments: this.attachments
      };
      if (this.bccsAllowed) {
        msgData.bcc = this.processRecipients(this.rcptsBcc);
      }
      axios.post('/rest/user/folders/outbox', msgData, {
        onUploadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            this.sendProgress = Math.floor(progressEvent.loaded / progressEvent.total * 100);
          } else {
            this.sendProgress = 100;
          }
        } })
        .then(d => {
          console.log('sent message', d.data);
        })
        .catch(e => {
          console.log(e);
          this.$dialog.alert('failed - ' + e.message);
        })
        .finally(() => {
          this.sending = false;
          this.$store.commit('setEditorMode', 0);
        });
    },
    processRecipients(rcpts) {
      return rcpts.map(r => {
        const x = ParseAddrs(r);
        if (x.length > 0) {
          const addr = x[0];
          if (addr.nameAuto) {
            return { name: '', addr: addr.addr };
          } else {
            return { name: addr.name, addr: addr.addr };
          }
        }
      });
    },
    getEditorStyleSheet() {
      var file = this.editorDoc.getElementById('css');
      console.log(this.editorDoc, file);
      var content = Array.prototype.map.call(file.sheet.cssRules, x => x.cssText).join('\n');
      console.log('stylesheet', content);
      return content;
    }
  },
  computed: {
    justifyOptions() {
      return [
        { icon: 'align-left', value: 'justify.left', aria: this.$t('messageEditor.aria.align_left') },
        { icon: 'align-center', value: 'justify.center', aria: this.$t('messageEditor.aria.align_center') },
        { icon: 'align-right', value: 'justify.right', aria: this.$t('messageEditor.aria.align_right') },
        { icon: 'align-justify', value: 'justify.justify', aria: this.$t('messageEditor.aria.align_justify') }
      ];
    },
    headerOptions() {
      return [
        { icon: 'h1', value: 'header.h1', aria: this.$t('messageEditor.headerMenu.h1') },
        { icon: 'h2', value: 'header.h2', aria: this.$t('messageEditor.headerMenu.h2') },
        { icon: 'h3', value: 'header.h3', aria: this.$t('messageEditor.headerMenu.h3') }
      ];
    },
    textSizeOptions() {
      return [
        { value: 'textsize.small', text: this.$t('messageEditor.textSizeMenu.small') },
        { value: 'textsize.medium', text: this.$t('messageEditor.textSizeMenu.medium') },
        { value: 'textsize.large', text: this.$t('messageEditor.textSizeMenu.large') },
        { value: 'textsize.xx-large', text: this.$t('messageEditor.textSizeMenu.huge') }
      ];
    },
    fontOptions() {
      let fonts = [];
      for (let i = 1; i <= 20; i++) {
        const x = this.$t(`messageEditor.fontMenu.fonts_${i}`);
        if (x.substr(0, 13) !== 'messageEditor') {
          if (x === '') {
            fonts.push({ separator: true });
          } else {
            fonts.push({ value: `font.${i}`,
              text: this.$t(`messageEditor.fontMenu.fontsText_${i}`),
              style: `font-family: ${x}` });
          }
        } else {
          break;
        }
      }
      return fonts;
    },
    addLinkOK() {
      if (/^(f|ht)tps?:\/\/[a-z0-9.-]+\.[a-z]{2,10}/i.test(this.addLinkValue)) {
        return true;
      }
      if (/^mailto:[^@]+@[a-z0-9.-]+\.[a-z]{2,10}(\?|$)/i.test(this.addLinkValue)) {
        return true;
      }
      if (/^www\.[a-z0-9.-]+\.[a-z]{2,10}/i.test(this.addLinkValue)) {
        return true;
      }
      if (/^[^@]+@[a-z0-9.-]+\.[a-z]{2,10}(\?|$)/i.test(this.addLinkValue)) {
        return true;
      }
      return false;
    },
    subject1: {
      get() {
        return this.theSubject;
      },
      set(val) {
        this.$emit('updateSubject', val);
        this.theSubject = val;
      }
    },
    getBaseURL() {
      return process.env.VUE_APP_BASEURL;
    },
    attachmentsSize() {
      return this.attachments.reduce((s, a) => {
        return s + a.data.length * 3 / 4;
      }, 0);
    },
    senderAddresses() {
      return this.$store.getters.senderEmailAddresses.split('\n').map(a => {
        return { html: `${this.$store.state.settings.realname} &lt;${a}&gt;`, value: a };
      });
    },
    invalidMessage() {
      if ((this.rcptsTo.length === 0) && (this.rcptsCc.length === 0) && (this.rcptsBcc.length === 0)) {
        return true;
      }
      return false;
    },
    bccsAllowed() {
      return this.$store.state.settings.allowbccs;
    }
  }
};
</script>

<style scoped lang="scss">
    @import '../assets/bootstrap-style.scss';

    .editor {
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;

        .toolbar {
            flex: 0 0 30px;
            display: flex;
            flex-wrap: wrap;
            .toolbarSection {
                margin-right: 10px;
                flex: 0 0 auto;
                white-space: nowrap;
                height: 30px;
                .editbutton {
                    margin-left: 0;
                    padding: 5px;
                    transition: background-color 0.3s;
                    border-radius: 3px;
                    &.isActive {
                        background-color: $gray-300;
                        &:hover {
                            background-color: $gray-400;
                        }
                    }
                    &:hover {
                        background-color: $gray-300;
                    }
                }
            }

            border-bottom: $editor-toolbar-borderbottom;
        }

        .framewrap {
            flex: 1;
            padding: 0;

            textarea {
                width: 100%;
                height: 100%;
                border: 0;
                resize: none;
            }

            iframe {
                border: 0;
                width: 100%;
                height: 100%;
                overflow-y: hidden;
            }
        }

        .status {
            display: none;
            flex: 0 0 100px;
        }

        .sender {
            display: flex;
            label {
                flex: 0 0 auto;
                font-weight: bold;
                margin: 0 10px;
                min-width: 5em;
            }
            span {
                flex: 1;
            }
        }
        .recipients {
            max-height: 6em;
            overflow-y: auto;
        }
        .subject {
            input {
                padding: 0 10px;
                border: 0;
                outline: 0;
                width: 100%;
            }
        }
        .bottombar {
            border-top: $message-editor-separator;
            background-color: $message-editor-bottombar;
            display: flex;
            .send {
                flex: 0 0 200px;
            }
            .status {
                flex: 1;
                display: flex;
                flex-wrap: wrap;
              .messagesize {
                padding-left: 1em;
              }
              .loadingattachments {
                font-style: italic;
              }
            }
        }
    }
</style>
